import { FC, PropsWithChildren, useContext } from "react";

import { SubmitButton, SubmitButtonProps } from "capitalrx-components-2.0/lib/forms";
import { Button, MenuButton, MenuButtonProps } from "capitalrx-components-2.0/lib/general";
import { styled } from "styled-components";

import { ExperienceContext } from "@/App/context";

export const ThemedButton = styled(Button)`
  span {
    white-space: collapse;
  }
  font-family: "Source Sans 3", sans-serif;

  &.tertiary {
    color: ${(props) => props.theme?.primary_button_color};
    &:hover {
      color: ${(props) => props.theme?.primary_button_color};
    }

    path {
      fill: ${(props) => props.theme?.primary_button_color} !important;
    }

    &.disabled {
      color: ${(props) => props.theme?.primary_button_color} !important;
      cursor: not-allowed;
    }
    &:disabled {
      color: ${(props) => props.theme?.primary_button_color} !important;
      cursor: not-allowed;
    }

    &:active {
      color: ${(props) => props.theme?.primary_button_color} !important;
    }
  }

  &.secondary {
    border-color: ${(props) => props.theme?.primary_button_color};
    color: ${(props) => props.theme?.primary_button_color};

    &:hover&:not(:disabled) {
      border-color: ${(props) => props.theme?.primary_button_color};
      color: ${(props) => props.theme?.primary_button_color};
      background-color: ${(props) => props.theme?.primary_button_color}1A;
    }

    path {
      fill: ${(props) => props.theme?.primary_button_color} !important;
    }

    &.disabled {
      border-color: ${(props) => props.theme?.primary_button_color} !important;
      opacity: 0.5;
      color: ${(props) => props.theme?.primary_button_color} !important;
    }
    &:disabled {
      border-color: ${(props) => props.theme?.primary_button_color} !important;
      opacity: 0.5;
      color: ${(props) => props.theme?.primary_button_color} !important;
    }
  }

  &.primary {
    border-color: ${(props) => props.theme?.primary_button_color};
    background-color: ${(props) => props.theme?.primary_button_color};
    &:hover {
      border-color: ${(props) => props.theme?.primary_button_color};
      background-color: ${(props) => props.theme?.primary_button_color};
    }

    &.disabled {
      background-color: ${(props) => props.theme?.primary_button_color} !important;
      border-color: ${(props) => props.theme?.primary_button_color} !important;
      opacity: 0.5;
    }
    &:disabled {
      background-color: ${(props) => props.theme?.primary_button_color} !important;
      border-color: ${(props) => props.theme?.primary_button_color} !important;
      opacity: 0.5;
    }
  }
`;

const _ThemedSubmitButtonWrapper = styled.div`
  // display: flex;
  // width: 100%;

  > * {
    width: 100%;
  }

  span {
    white-space: collapse;
  }

  > .disabled {
    background-color: ${(props) => props.theme?.primary_button_color} !important;
    border-color: ${(props) => props.theme?.primary_button_color} !important;
    opacity: 0.5;
  }
  > :disabled {
    background-color: ${(props) => props.theme?.primary_button_color} !important;
    border-color: ${(props) => props.theme?.primary_button_color} !important;
    opacity: 0.5;
  }

  > .tertiary {
    color: ${(props) => props.theme?.primary_button_color};
    &:hover {
      color: ${(props) => props.theme?.primary_button_color};
    }

    path {
      fill: ${(props) => props.theme?.primary_button_color} !important;
    }
  }

  > .primary {
    border-color: ${(props) => props.theme?.primary_button_color};
    background-color: ${(props) => props.theme?.primary_button_color};
    &:hover,
    &:active {
      border-color: ${(props) => props.theme?.primary_button_color};
      background-color: ${(props) => props.theme?.primary_button_color};
    }
  }
`;

const _ThemedSubmitButton: FC<PropsWithChildren & SubmitButtonProps> = (props) => {
  return (
    <_ThemedSubmitButtonWrapper>
      <SubmitButton {...props}>{props.children}</SubmitButton>
    </_ThemedSubmitButtonWrapper>
  );
};

export const ThemedSubmitButton = _ThemedSubmitButton;

export const ThemedMenuButton = styled(MenuButton)`
  > button.primary,
  > button.primary.down {
    border-color: ${(props) => props.theme?.primary_button_color};
    background-color: ${(props) => props.theme?.primary_button_color};
    &:hover,
    &:active {
      border-color: ${(props) => props.theme?.primary_button_color};
      background-color: ${(props) => props.theme?.primary_button_color};
    }
  }

  > button.tertiary,
  button.tertiary.down {
    color: ${(props) => props.theme?.primary_button_color};
    &:hover {
      color: ${(props) => props.theme?.primary_button_color};
    }

    path {
      fill: ${(props) => props.theme?.primary_button_color};
    }
  }
`;

/**
 * The math behind all this can be found in the following gist:
 * https://gist.github.com/Myndex/e1025706436736166561d339fd667493#lets-flip-for-color
 * There are different ways to calculate contrast but this is a good starting point.
 * If there are minor issues with this function, we can change the numbers to tweak the flip
 * point but if there are bigger issues or clients complain then we can make more substantial changes
 */

const hex2rgb = (hex: string) => {
  if (hex.slice(0, 1) === "#") {
    hex = hex.slice(1);
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return { r, g, b };
};

export const ThemedTopNavButton = (props: MenuButtonProps) => {
  const { experience } = useContext(ExperienceContext);

  const { r, g, b } = hex2rgb(experience.theme.primary_color);

  const ys = Math.pow(r / 255.0, 2.2) * 0.2126 + Math.pow(g / 255.0, 2.2) * 0.7152 + Math.pow(b / 255.0, 2.2) * 0.0722;
  const textColor = ys < 0.38 ? "#ffffff" : "#000000";

  return <StyledTopNavButton {...props} $textColor={textColor} />;
};

const StyledTopNavButton = styled(MenuButton)<{ $textColor?: string }>`
  &.menu-button {
    min-width: 0;

    &:first-of-type {
      min-width: fit-content;
    }
  }

  > button.primary,
  > button.primary.down,
  > button.tertiary,
  > button.tertiary.down {
    border-color: transparent;
    background-color: transparent;
    color: ${(props) => props.$textColor};
    font-family: "Source Sans 3", sans-serif;
    font-weight: 600;
    font-size: 14px;
    font-style: normal;
    line-height: 20px;
    letter-spacing: 0px;
    max-width: 100%;

    path {
      fill: ${(props) => props.$textColor};
    }

    &:hover,
    &:active {
      border-color: transparent;
      background-color: transparent;
      text-decoration: underline;
      color: ${(props) => props.$textColor};
      path {
        fill: ${(props) => props.$textColor};
      }
    }

    div {
      min-width: 0;
    }

    span {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    span.overflow-ellipsis {
      text-overflow: ellipsis;
      display: block;
      overflow: hidden;
    }

    svg {
      width: 16px;
      height: 16px;
      display: inline-block;
      vertical-align: middle;
      overflow: visible;
    }
  }
`;
